<template>
  <div v-if="activeTab === 'services'" class="card card-outline-primary rounded-3 shadow">
    <div class="card-body p-0 px-4 py-3">
      <div class="d-flex justify-content-between align-items-center">
        <ul class="card-progress-tracker fw-bold text-center mb-0">
          <li :class="(workflowData && workflowData.workflow_completed && workflowData.workflow_completed.workflow_services_exist) ? 'completed': 'in-completed'">
            Services Selected
          </li>
        </ul>
      </div>
      <transition name="slide-fade">
        <div class="row">
          <hr class="fw-light" />
          <!-- Left Panel: Services List -->
          <div class="col-md-4 col-lg-3 service-panel-container">
            <div class="service-nav-panel">
              <div class="service-list">
                <div
                  v-for="service in workflowData.services"
                  :key="service.id"
                  class="service-item"
                  :class="{ active: selectedService && selectedService.id === service.id }"
                  @click="selectService(service)"
                >
                  <div class="service-name">
                    {{ capitalizeString(service.service.service_name) }}
                    <span v-if="service.safps_configuration && service.safps_configuration.safps_enabled" class="badge bg-info text-white ms-1" style="font-size: 0.6rem;">
                      SAFPS
                    </span>
                  </div>
                  <div class="service-description">
                    {{ service.service?.service_details[0]?.service_description || 'No description available' }}
                  </div>
                  <div class="service-cost">
                    <span class="text-muted">Cost:</span>
                    <span class="fw-bold text-primary" v-if="service.service.service_details.length > 0">
                      <span v-if="service.safps_configuration && service.safps_configuration.safps_enabled">
                        R {{ service.service.service_details[0].service_cost }}
                      </span>
                      <span v-else>
                        R {{ service.service.service_details[0].service_cost }}
                      </span>
                    </span>
                    <span class="fw-bold text-primary" v-else>
                      R 0.00
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- Right Panel: Service Details -->
          <div class="col-md-8 col-lg-9 main-content-container">
            <div class="main-service-content">
              <div v-if="!selectedService" class="no-service-selected">
                <div class="text-center text-muted py-5">
                  <i class="bi bi-info-circle fs-1 mb-3"></i>
                  <p class="mb-0">Select a service from the list to view details</p>
                </div>
              </div>
              <div v-if="selectedService" class="service-details">
                <!-- Service Header -->
                <div class="service-header mb-4">
                  <div class="d-flex align-items-center mb-2">
                    <div class="service-icon me-2">
                      <i class="bi bi-gear-fill text-primary"></i>
                    </div>
                    <h5 class="fs-6 fw-bold mb-0 text-dark">
                      {{ capitalizeString(selectedService.service.service_name) }}
                      <span v-if="selectedService.safps_configuration && selectedService.safps_configuration.safps_enabled" class="badge bg-info text-white ms-1" style="font-size: 0.6rem;">
                        SAFPS
                      </span>
                    </h5>
                  </div>
                  <p class="text-xs text-muted mb-0 lh-sm">
                    Service details and configuration overview
                  </p>
                </div>
                <!-- Service Details Section -->
                <div class="settings-section">
                  <div class="card border">
                    <div class="card-body p-3">
                      <div class="row g-3">
                        <div class="col-md-6">
                          <div class="mb-3">
                            <label class="form-label fw-medium text-xs">Service Description</label>
                            <p class="text-muted small">{{ selectedService.service?.service_details[0]?.service_description || 'No description available' }}</p>
                          </div>
                        </div>
                        <div class="col-md-6">
                          <div class="mb-3">
                            <label class="form-label fw-medium text-xs">Cost Per Request</label>
                            <p class="text-primary fw-bold" v-if="selectedService.service.service_details.length > 0">
                              <span v-if="selectedService.safps_configuration && selectedService.safps_configuration.safps_enabled">
                                R {{ selectedService.service.service_details[0].service_cost }}
                                <br>
                                <small class="text-muted">
                                  (Base: R{{ selectedService.service.service_details[0].base_service_cost }} + SAFPS: R{{ selectedService.safps_configuration.safps_cost }})
                                </small>
                              </span>
                              <span v-else>
                                R {{ selectedService.service.service_details[0].service_cost }}
                              </span>
                            </p>
                            <p class="text-primary fw-bold" v-else>R 0.00</p>
                          </div>
                        </div>
                        <div class="col-md-6" v-if="service_assigned_information?.service?.service_type?.service_type === 'system generated'">
                          <div class="mb-3">
                            <label class="form-label fw-medium text-xs">Service Inputs</label>
                            <div class="small">
                              <div v-if="is_loading" class="d-flex align-items-center py-2">
                                <div class="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
                                <span class="text-muted">Loading inputs...</span>
                              </div>
                              <ul v-else-if="custom_form_questions.length">
                                <li v-for="(question, idx) in custom_form_questions" :key="idx">
                                  {{ question.question }}
                                </li>
                              </ul>
                              <ul v-else-if="parsedInputs.length">
                                <li v-for="(input, idx) in parsedInputs" :key="idx">
                                  {{ input }}
                                </li>
                              </ul>
                              <div v-else class="text-muted">No inputs available</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <!-- Service Configuration Section -->
                <div v-if="is_async || is_prepopulation_of_data || is_background || is_custom" class="settings-section mt-4">
                  <div v-if="is_loading" class="d-flex justify-content-center align-items-center p-3">
                    <div class="spinner-border text-primary" role="status">
                      <span class="visually-hidden">Loading...</span>
                    </div>
                  </div>
                  <div v-else-if="service_assigned_information" class="configuration-panel">
                    <h6 class="fw-bold mb-3 text-muted text-xs ">Service Configurations</h6>
                    <div class="row g-3">
                      <div class="col-md-6" v-if="is_async">
                        <div class="config-item d-flex align-items-center justify-content-between p-3 bg-light rounded">
                          <label class="form-label fw-medium text-xs mb-0">Run Async</label>
                          <span :class="['badge', service_assigned_information.run_async ? 'bg-success' : 'bg-secondary']">
                            {{ service_assigned_information.run_async ? 'Enabled' : 'Disabled' }}
                          </span>
                        </div>
                      </div>
                      <div class="col-md-6" v-if="is_prepopulation_of_data">
                        <div class="config-item d-flex align-items-center justify-content-between p-3 bg-light rounded">
                          <label class="form-label fw-medium text-xs mb-0">Prepopulation of Data</label>
                          <span :class="['badge', service_assigned_information.has_prepopulation ? 'bg-success' : 'bg-secondary']">
                            {{ service_assigned_information.has_prepopulation ? 'Enabled' : 'Disabled' }}
                          </span>
                        </div>
                      </div>
                      <div class="col-md-6" v-if="is_background">
                        <div class="config-item d-flex align-items-center justify-content-between p-3 bg-light rounded">
                          <label class="form-label fw-medium text-xs mb-0">Run in Background</label>
                          <span :class="['badge', service_assigned_information.is_background ? 'bg-success' : 'bg-secondary']">
                            {{ service_assigned_information.is_background ? 'Enabled' : 'Disabled' }}
                          </span>
                        </div>
                      </div>
                      <div class="col-md-6" v-if="is_prepopulation_of_data">
                        <div class="config-item d-flex align-items-center justify-content-between p-3 bg-light rounded">
                          <label class="form-label fw-medium text-xs mb-0">Use Prepopulation</label>
                          <span :class="['badge', service_assigned_information.use_prepopulation ? 'bg-success' : 'bg-secondary']">
                            {{ service_assigned_information.use_prepopulation ? 'Enabled' : 'Disabled' }}
                          </span>
                        </div>
                      </div>
                      <div class="col-md-6" v-if="is_custom">
                        <div class="config-item d-flex align-items-center justify-content-between p-3 bg-light rounded">
                          <label class="form-label fw-medium text-xs mb-0">SAFPS Enabled</label>
                          <span :class="['badge', service_assigned_information.allow_safps ? 'bg-success' : 'bg-secondary']">
                            {{ service_assigned_information.allow_safps ? 'Enabled' : 'Disabled' }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import Swal from "sweetalert2";
import { API_BASE_URL } from "@/config";

export default {
  name: 'ServicesTab',
  props: {
    activeTab: {
      type: String,
      default: 'services'
    },
    workflowData: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      selectedService: null,
      service_assigned_information: null,
      is_loading: false,
      async_list: [],
      background_list: [
        "Address Match",
        "Anti Money Laundering",
        "Eligibility Solution",
        "Telephones"
      ],
      custom_settings: ["RSA ID Verification"],
      prepopulation_of_data_services: [
        "OCR Liveness Verification",
        "ID Verification (Liveness check)",
        "Alternative Liveness Verification",
        "Enterprise KYB",
        "Enterprise Credit Check",
        "CIPC Verification",
        "Bank Account Company Verification",
        "RSA ID Verification"
      ],
      custom_form_questions: []
    };
  },
  computed: {
    is_async() {
      return this.async_list.includes(this.selectedService?.service.service_name);
    },
    is_background() {
      return this.background_list.includes(this.selectedService?.service.service_name);
    },
    is_prepopulation_of_data() {
      return this.prepopulation_of_data_services.includes(this.selectedService?.service.service_name);
    },
    is_custom() {
      return this.custom_settings.includes(this.selectedService?.service.service_name);
    },
    parsedInputs() {
      if (!this.selectedService || !this.selectedService.service.service_details.length) return [];
      try {
        return JSON.parse(this.selectedService.service.service_details[0].service_input.replace(/'/g, '"'));
      } catch {
        return [];
      }
    },
    parsedOutputs() {
      if (!this.selectedService || !this.selectedService.service.service_details.length) return [];
      try {
        return JSON.parse(this.selectedService.service.service_details[0].service_output.replace(/'/g, '"'));
      } catch {
        return [];
      }
    }
  },
  methods: {
    capitalizeString(string) {
      return string.charAt(0).toUpperCase() + string.slice(1);
    },
    selectService(service) {
      this.selectedService = service;
      this.custom_form_questions = [];
      this.get_selected_workflow_service();
    },
    openModal(service) {
      this.$emit('open-modal', service);
    },
    async get_selected_workflow_service() {
      if (!this.selectedService) return;
      this.is_loading = true;
      const token = localStorage.getItem("token");
      const api_url = `${API_BASE_URL}/verify_api/workflow_management_api/get_selected_workflow_service/?service_id=${this.selectedService.service.id}&workflow_id=${this.workflowData.id}`;

      try {
        const response = await fetch(api_url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Token ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Network response was not ok');
        }

        const data = await response.json();
        this.service_assigned_information = data.data || null;
        this.get_custom_form_details(this.selectedService.service.id);
      } catch (e) {
        console.error('Error fetching service settings:', e);
        Swal.fire('Error', 'Failed to load service settings', 'error');
      } finally {
        this.is_loading = false;
      }
    },
    async get_custom_form_details(serviceId) {
      const token = localStorage.getItem("token");
      const api_url = `${API_BASE_URL}/verify_api/service_management_api/get_custom_form_details/${serviceId}/`;
      try {
        const response = await fetch(api_url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Token ${token}`,
          },
        });
        if (!response.ok) throw new Error('Network response was not ok');
        const data = await response.json();
        if (data.form_details && Array.isArray(data.form_details.questions)) {
          this.custom_form_questions = data.form_details.questions;
        } else if (Array.isArray(data.questions)) {
          this.custom_form_questions = data.questions;
        } else {
          this.custom_form_questions = [];
        }
      } catch (e) {
        this.custom_form_questions = [];
      }
    }
  },
  watch: {
    workflowData: {
      handler(newVal) {
        if (newVal && newVal.services && newVal.services.length > 0 && !this.selectedService) {
          this.selectedService = newVal.services[0];
          this.get_selected_workflow_service();
        }
      },
      immediate: true
    }
  },
  mounted() {
    if (this.workflowData && this.workflowData.services && this.workflowData.services.length > 0) {
      this.selectedService = this.workflowData.services[0];
      this.get_selected_workflow_service();
    }
  }
}
</script>

<style scoped>
.service-panel-container {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}

.main-content-container {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.service-nav-panel {
  border-right: 1px solid #e9ecef;
  height: 600px;
  overflow-y: auto;
  padding-right: 20px;
  padding-left: 4px;
}

.service-list {
  max-height: 520px;
  overflow-y: auto;
  padding-right: 4px;
}

.service-item {
  padding: 16px;
  border: 2px solid transparent;
  border-radius: 12px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  position: relative;
  overflow: hidden;
}

.service-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  opacity: 0;
  transition: opacity 0.3s ease;
  background: var(--primary-color, #007bff);
}

.service-item:hover {
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  border-color: var(--primary-color, #007bff);
}

.service-item:hover::before {
  opacity: 1;
}

.service-item.active {
  border-color: var(--primary-color, #007bff);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.service-item.active::before {
  opacity: 1;
}

.service-name {
  font-weight: 600;
  font-size: 15px;
  margin-bottom: 6px;
  color: var(--bs-dark, #212529);
  line-height: 1.4;
}

.service-item.active .service-name {
  color: var(--primary-color, #007bff);
}

.service-description {
  font-size: 13px;
  color: var(--bs-secondary, #6c757d);
  margin-bottom: 10px;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-clamp: 2;
  overflow: hidden;
}

.service-cost {
  font-size: 12px;
}

.main-service-content {
  padding: 1rem;
}

.no-service-selected {
  text-align: center;
  padding: 2rem;
}

.service-details {
  padding: 1.5rem;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.service-header {
  padding-bottom: 1rem;
  border-bottom: 1px solid #e9ecef;
}

.service-icon {
  width: 32px;
  height: 32px;
  background: #f8f9fa;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #dee2e6;
}

.service-icon i {
  font-size: 1rem;
}

.settings-section .card {
  border-radius: 8px;
}

.form-label.fw-medium {
  font-weight: 500;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .service-panel-container {
    margin-bottom: 1rem;
  }
  
  .main-content-container {
    padding-left: 0;
  }
  
  .service-nav-panel {
    height: auto;
    max-height: 300px;
  }
}
</style>
